import React, { useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

export const COLORS = [
  '#1565C0', '#2E7D32', '#6A1B9A', '#BF360C',
  '#00695C', '#AD1457', '#283593', '#558B2F',
];
const LIC_COLOR = '#FF8F00';

function fmtM(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  return `${names[(parseInt(mo, 10) || 1) - 1]} ${(y || '').slice(2)}`;
}

export function buildBudgetChartData(months, resources, activities, licenses) {
  return (months || []).map((m, mi) => {
    const row = { month: fmtM(m) };
    let totalHrs = 0;
    (resources || []).forEach(res => {
      const hrs = (activities || []).reduce((s, act) =>
        s + (act.resourceLines || [])
          .filter(l => l.resourceId === res.id)
          .reduce((ls, l) => ls + (parseFloat(l.months?.[m]) || 0), 0), 0);
      row[`r${res.id}`] = Math.round(hrs * (parseFloat(res.hourlyRate) || 0));
      totalHrs += hrs;
    });
    let licSek = 0;
    (licenses || []).forEach(lic => {
      const cost = (parseFloat(lic.price) || 0) * (parseFloat(lic.quantity) || 1);
      if (lic.priceType === 'monthly') licSek += cost;
      else if (lic.priceType === 'quarterly' && mi % 3 === 0) licSek += cost;
      else if (lic.priceType === 'yearly'    && mi % 12 === 0) licSek += cost;
      else if (lic.priceType === 'fixed'     && mi === 0) licSek += cost;
    });
    row.lic = Math.round(licSek);
    row.tim = Math.round(totalHrs * 10) / 10;
    return row;
  });
}

export default function BudgetChart({
  months, resources, activities, licenses,
  height = 260,
  // Pass printWidth to render a fixed-size chart (no ResponsiveContainer) for PDF/print
  printWidth,
}) {
  const data = useMemo(
    () => buildBudgetChartData(months, resources, activities, licenses),
    [months, resources, activities, licenses],
  );

  if (!data.length) return null;

  const hasLicenses = (licenses || []).some(
    l => (parseFloat(l.price) || 0) * (parseFloat(l.quantity) || 1) > 0,
  );
  const hasHours = data.some(d => d.tim > 0);
  const margin = { top: 10, right: hasHours ? 52 : 16, left: 4, bottom: 4 };

  const fmtKr = v =>
    v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` :
    v >= 1000 ? `${Math.round(v / 1000)}k` : String(v);

  const tooltipFmt = (value, name) =>
    name === 'Tim/mån'
      ? [`${value} h`, name]
      : [`${Math.round(value).toLocaleString('sv-SE')} kr`, name];

  // Chart elements as variables so both render paths share the same config
  const grid    = <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8e8e8" />;
  const xAxis   = <XAxis dataKey="month" tick={{ fontSize: 9 }} />;
  const yKr     = <YAxis yAxisId="kr" orientation="left" tickFormatter={fmtKr} tick={{ fontSize: 9 }} width={46} />;
  const yTim    = hasHours ? <YAxis yAxisId="tim" orientation="right" tick={{ fontSize: 9 }} width={36} unit=" h" /> : null;
  const tip     = <Tooltip formatter={tooltipFmt} />;
  const leg     = <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />;
  const resBars = (resources || []).map((res, i) => (
    <Bar key={res.id} yAxisId="kr" dataKey={`r${res.id}`} stackId="s"
      fill={res.color || COLORS[i % COLORS.length]} name={res.name || '(namnlös)'}
      maxBarSize={40} isAnimationActive={false} />
  ));
  const licBar  = hasLicenses
    ? <Bar yAxisId="kr" dataKey="lic" stackId="s" fill={LIC_COLOR} name="Licenser" maxBarSize={40} isAnimationActive={false} />
    : null;
  const timLine = hasHours
    ? <Line yAxisId="tim" dataKey="tim" type="monotone" stroke="#1a1a1a" strokeWidth={2}
        dot={{ r: 2, fill: '#1a1a1a' }} activeDot={{ r: 4 }} name="Tim/mån" isAnimationActive={false} />
    : null;

  if (printWidth) {
    return (
      <ComposedChart width={printWidth} height={height} data={data} margin={margin}>
        {grid}{xAxis}{yKr}{yTim}{tip}{leg}{resBars}{licBar}{timLine}
      </ComposedChart>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={margin}>
        {grid}{xAxis}{yKr}{yTim}{tip}{leg}{resBars}{licBar}{timLine}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
